'use client';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import CibWindows from '../icons/CibWindows';
import ReqTooltip from './ReqTooltip';
import { ButtonProps } from './types';

const NeonButton = (props: ButtonProps) => {
  const { className, href, onClick, children } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showReqDrawer, setShowReqDrawer] = useState(false);

  // 状态
  const buttonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const rippleRef = useRef<HTMLSpanElement>(null);
  const buttonContentRef = useRef<HTMLDivElement>(null);
  const [isActive, setIsActive] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isGlitching, setIsGlitching] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // 使用 useRef 存储目标位置，而不是 useState
  const targetPositionRef = useRef({ x: 0, y: 0 });

  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const glitchTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 触发故障效果
  const triggerGlitchEffect = useCallback(() => {
    setIsGlitching(true);
    setTimeout(() => setIsGlitching(false), 300);
  }, []);

  // 开始随机故障效果
  const startRandomGlitchEffect = useCallback(() => {
    // 清除之前的定时器
    if (glitchTimerRef.current) {
      clearTimeout(glitchTimerRef.current);
    }

    // 只有在悬停状态下才继续随机故障
    if (isHovered) {
      // 随机 2-5 秒后触发故障效果
      const randomDelay = Math.floor(Math.random() * 3000) + 2000;
      glitchTimerRef.current = setTimeout(() => {
        triggerGlitchEffect();
        startRandomGlitchEffect(); // 递归调用以继续随机故障
      }, randomDelay);
    }
  }, [isHovered, triggerGlitchEffect]);

  // 处理鼠标进入
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    startRandomGlitchEffect();
  }, [startRandomGlitchEffect]);

  // 处理鼠标离开
  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);

    // 停止随机故障效果
    if (glitchTimerRef.current) {
      clearTimeout(glitchTimerRef.current);
      glitchTimerRef.current = null;
    }

    // 重置鼠标位置到按钮中心
    if (buttonContainerRef.current) {
      const width = buttonContainerRef.current.offsetWidth;
      const height = buttonContainerRef.current.offsetHeight;
      targetPositionRef.current = { x: width / 2, y: height / 2 };
    }
  }, []);

  // 处理鼠标移动，更新目标位置
  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!buttonContainerRef.current) return;

      const rect = buttonContainerRef.current.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      targetPositionRef.current = { x, y };
      triggerGlitchEffect();
    },
    [triggerGlitchEffect],
  );

  // 平滑动画效果 - 使用 requestAnimationFrame 实现
  useEffect(() => {
    // 动画函数
    const animatePosition = () => {
      setMousePosition((prev) => {
        // 平滑过渡到目标位置
        const dx = (targetPositionRef.current.x - prev.x) * 0.15;
        const dy = (targetPositionRef.current.y - prev.y) * 0.15;

        return {
          x: prev.x + dx,
          y: prev.y + dy,
        };
      });

      // 继续下一帧动画
      animationFrameRef.current = requestAnimationFrame(animatePosition);
    };

    // 启动动画
    animationFrameRef.current = requestAnimationFrame(animatePosition);

    // 清理函数
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []); // 空依赖数组，只在组件挂载时运行一次

  // 初始化鼠标位置为按钮中心
  useEffect(() => {
    if (!buttonContainerRef.current) return;

    const width = buttonContainerRef.current.offsetWidth;
    const height = buttonContainerRef.current.offsetHeight;

    // 初始化鼠标位置和目标位置
    setMousePosition({ x: width / 2, y: height / 2 });
    targetPositionRef.current = { x: width / 2, y: height / 2 };

    // 组件挂载后随机触发一次故障效果
    const initialDelay = Math.floor(Math.random() * 1000) + 500;
    const timer = setTimeout(() => {
      triggerGlitchEffect();
    }, initialDelay);

    return () => clearTimeout(timer);
  }, [triggerGlitchEffect]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (glitchTimerRef.current) {
        clearTimeout(glitchTimerRef.current);
      }
    };
  }, []);

  // 创建波纹效果
  const createRipple = useCallback((event: React.MouseEvent<HTMLElement>) => {
    const button = event.currentTarget;
    const rippleContainer = rippleRef.current;

    if (!rippleContainer) return;

    // 清除现有的波纹
    const existingRipples = rippleContainer.querySelectorAll('.ripple');
    existingRipples.forEach((ripple) => {
      ripple.remove();
    });

    // 创建新的波纹元素
    const circle = document.createElement('span');
    const diameter = Math.max(button.clientWidth, button.clientHeight) * 2;
    const radius = diameter / 2;

    // 获取点击位置相对于按钮的坐标
    const rect = button.getBoundingClientRect();
    const left = event.clientX - rect.left - radius;
    const top = event.clientY - rect.top - radius;

    // 设置波纹样式
    circle.style.width = circle.style.height = `${diameter}px`;
    circle.style.left = `${left}px`;
    circle.style.top = `${top}px`;
    circle.classList.add('ripple');

    // 添加波纹到容器
    rippleContainer.appendChild(circle);

    // 动画结束后移除波纹
    setTimeout(() => {
      circle.remove();
    }, 600);
  }, []);

  // 处理点击事件
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      // 如果是移动设备，显示 ReqDrawer 而不是执行默认操作
      if (isMobile) {
        event.preventDefault();
        event.stopPropagation();
        setShowReqDrawer(true);
        return;
      }

      createRipple(event);
      setIsActive(true);

      // 触发故障效果
      triggerGlitchEffect();

      setTimeout(() => setIsActive(false), 150);
      if (onClick) {
        onClick();
      }
    },
    [onClick, createRipple, triggerGlitchEffect, isMobile],
  );

  // 基础类名
  const baseClasses = `
    button 
    relative 
    inline-flex 
    items-center 
    justify-center 
    h-11 
    transition-all 
    duration-300
    ${isActive ? 'scale-[0.98] translate-y-[1px]' : ''}
    px-4 
    text-n-1
    cursor-pointer
    ${className || ''}
    group
    overflow-hidden
  `;

  // 按钮主体样式
  const buttonBodyClasses = `
    absolute 
    inset-0 
    bg-[#0f1923]/90
    rounded-lg
  `;

  // 按钮背景特效
  const backgroundEffectClasses = `
    absolute 
    inset-0 
    bg-gradient-to-br 
    from-[#0f1923] 
    to-[#1a2530]
    opacity-80
    rounded-lg
    overflow-hidden
  `;

  // 背景网格线
  const gridLinesClasses = `
    absolute 
    inset-0 
    opacity-20
    pointer-events-none
  `;

  // 四角顶角装饰
  const cornerDecorations = (
    <>
      {/* 左上角 - 蓝色 */}
      <div className="absolute left-0 top-0 h-[12px] w-[12px] overflow-hidden">
        <div className="absolute left-0 top-0 h-[10px] w-[10px] rounded-tl-md border-l-2 border-t-2 border-[#00e4ff]"></div>
      </div>

      {/* 右上角 - 紫色 */}
      <div className="absolute right-0 top-0 h-[12px] w-[12px] overflow-hidden">
        <div className="absolute right-0 top-0 h-[10px] w-[10px] rounded-tr-md border-r-2 border-t-2 border-[#8e33ff]"></div>
      </div>

      {/* 左下角 - 绿色 */}
      <div className="absolute bottom-0 left-0 h-[12px] w-[12px] overflow-hidden">
        <div className="absolute bottom-0 left-0 h-[10px] w-[10px] rounded-bl-md border-b-2 border-l-2 border-[#00ff9e]"></div>
      </div>

      {/* 右下角 - 橙色 */}
      <div className="absolute bottom-0 right-0 h-[12px] w-[12px] overflow-hidden">
        <div className="absolute bottom-0 right-0 h-[10px] w-[10px] rounded-br-md border-b-2 border-r-2 border-[#fa541c]"></div>
      </div>
    </>
  );

  // 按钮边框发光效果
  const glowEffectClasses = `
    absolute 
    inset-0 
    rounded-lg
    opacity-30
    group-hover:opacity-60
    transition-opacity
    duration-300
    -z-10
  `;

  // 简化的故障效果
  const simpleGlitchEffect = (
    <div
      className={`absolute inset-0 overflow-hidden rounded-lg transition-opacity duration-150 ${isGlitching ? 'opacity-100' : 'opacity-0'}`}
    >
      {/* 水平故障线 */}
      <div className="absolute left-0 top-[30%] h-[1px] w-full bg-white/70"></div>
      <div className="absolute left-0 top-[70%] h-[1px] w-full bg-white/70"></div>

      {/* 故障块 */}
      <div className="absolute right-[10%] top-[20%] h-[5px] w-[30%] bg-white/20"></div>
      <div className="absolute left-[5%] top-[60%] h-[3px] w-[20%] bg-white/20"></div>

      {/* 故障位移效果 */}
      <div className={`absolute inset-0 ${isGlitching ? 'animate-glitch' : ''}`}>
        <div className="absolute inset-x-0 inset-y-[25%] h-[10%] -translate-x-[3px] bg-[#0f1923]/90"></div>
        <div className="absolute inset-x-0 inset-y-[45%] h-[10%] translate-x-[3px] bg-[#0f1923]/90"></div>
      </div>
    </div>
  );

  // 跟随鼠标的光效 - 差异化设计
  const mouseFollowEffects = (
    <>
      {/* 主光效 - 霓虹环形光晕 */}
      <div
        className="pointer-events-none absolute h-[200px] w-[200px] rounded-full opacity-0 mix-blend-screen transition-opacity duration-300 [@media(hover:hover)]:group-hover:opacity-40"
        style={{
          left: `${mousePosition.x - 100}px`,
          top: `${mousePosition.y - 100}px`,
          background:
            'radial-gradient(circle, rgba(0,228,255,0.7) 0%, rgba(142,51,255,0.4) 40%, rgba(0,255,158,0) 70%)',
          filter: 'blur(20px)',
        }}
      ></div>

      {/* 次级光效 - 霓虹光线 */}
      <div
        className="pointer-events-none absolute h-[2px] w-[300px] opacity-0 transition-opacity duration-300 [@media(hover:hover)]:group-hover:opacity-60"
        style={{
          left: `${mousePosition.x - 150}px`,
          top: `${mousePosition.y}px`,
          background: 'linear-gradient(90deg, rgba(0,228,255,0) 0%, rgba(0,228,255,0.8) 50%, rgba(0,228,255,0) 100%)',
          transform: `rotate(${Math.atan2(mousePosition.y - (buttonContainerRef.current?.offsetHeight || 0) / 2, mousePosition.x - (buttonContainerRef.current?.offsetWidth || 0) / 2) * (180 / Math.PI)}deg)`,
          transformOrigin: 'center',
        }}
      ></div>

      {/* 焦点光效 - 小光点 */}
      <div
        className="pointer-events-none absolute h-[10px] w-[10px] rounded-full opacity-0 transition-opacity duration-150 [@media(hover:hover)]:group-hover:opacity-100"
        style={{
          left: `${mousePosition.x - 5}px`,
          top: `${mousePosition.y - 5}px`,
          background: 'radial-gradient(circle, rgba(0,228,255,1) 0%, rgba(0,228,255,0.3) 70%, rgba(0,228,255,0) 100%)',
          boxShadow: '0 0 10px 2px rgba(0,228,255,0.7)',
        }}
      ></div>

      {/* 交叉光线效果 */}
      <div
        className="pointer-events-none absolute h-[1px] w-full opacity-0 transition-opacity duration-300 [@media(hover:hover)]:group-hover:opacity-30"
        style={{
          top: `${mousePosition.y}px`,
          background: 'linear-gradient(90deg, rgba(0,228,255,0) 0%, rgba(0,228,255,0.5) 50%, rgba(0,228,255,0) 100%)',
        }}
      ></div>
      <div
        className="pointer-events-none absolute h-full w-[1px] opacity-0 transition-opacity duration-300 [@media(hover:hover)]:group-hover:opacity-30"
        style={{
          left: `${mousePosition.x}px`,
          background: 'linear-gradient(0deg, rgba(142,51,255,0) 0%, rgba(142,51,255,0.5) 50%, rgba(142,51,255,0) 100%)',
        }}
      ></div>
    </>
  );

  // 新增：按钮外部的微弱光效
  const externalGlowEffect = (
    <div
      className="pointer-events-none absolute -inset-4 -z-20 opacity-0 transition-opacity duration-500 [@media(hover:hover)]:group-hover:opacity-30"
      style={{
        background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(0,228,255,0.15) 0%, rgba(142,51,255,0.1) 50%, rgba(0,0,0,0) 70%)`,
        filter: 'blur(8px)',
      }}
    ></div>
  );

  // 装饰元素
  const decorationElements = (
    <>
      <span className="absolute left-[15%] right-[15%] top-[6px] h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent"></span>
      <span className="absolute bottom-[6px] left-[15%] right-[15%] h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent"></span>
    </>
  );

  // 扫描线动画 - 橙色主题
  const scanlineElement = (
    <span className="absolute left-0 top-0 h-full w-full -translate-x-full bg-gradient-to-r from-transparent via-[#ff8c19]/10 to-transparent [@media(hover:hover)]:group-hover:animate-scanline"></span>
  );

  // 背景噪点效果
  const noiseElement = <span className="bg-noise absolute inset-0 rounded-lg opacity-10"></span>;

  // 按钮内容和req标签
  const buttonContent = (
    <div
      ref={buttonContentRef}
      className={`relative z-10 flex items-center gap-3 ${isGlitching ? 'animate-glitchText' : ''}`}
    >
      <Box component={CibWindows} className="h-4 w-4 opacity-90" style={{ color: 'white' }} />
      <span className="text-base font-bold tracking-wider">{children}</span>
      <ReqTooltip
        showDrawer={showReqDrawer}
        onDrawerClose={() => setShowReqDrawer(false)}
        downloadUrl={href || ''}
        buttonType="neon"
      />
    </div>
  );

  // 使用 React 的动态组件特性减少重复代码
  const Tag = href ? 'a' : 'button';

  // 处理共同和特定属性
  const commonProps = {
    className: baseClasses,
    onClick: handleClick,
    onMouseMove: handleMouseMove,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    style: { cursor: 'pointer' }, // 直接在元素上设置光标样式
  };

  // 确定最终的 props (添加 href 如果是链接)
  const finalProps = {
    ...commonProps,
    ...(href
      ? {
          href,
          ref: buttonRef as React.RefObject<HTMLAnchorElement>,
        }
      : {
          ref: buttonRef as React.RefObject<HTMLButtonElement>,
        }),
  };

  return (
    // @ts-ignore - TypeScript 限制，但这是有效的 JSX
    <Tag {...finalProps}>
      {/* 按钮容器 - 用于跟踪鼠标位置 */}
      <div ref={buttonContainerRef} className="absolute inset-0 z-0"></div>

      {/* 按钮外部的微弱光效 */}
      {externalGlowEffect}

      {/* 主体背景 */}
      <span className={buttonBodyClasses}></span>

      {/* 背景特效 */}
      <span className={backgroundEffectClasses}>
        {/* 背景网格线 */}
        <div className={gridLinesClasses}>
          <div className="bg-grid-lines h-full w-full"></div>
        </div>

        {/* 背景噪点 */}
        {noiseElement}
      </span>

      {/* 四角顶角装饰 */}
      {cornerDecorations}

      {/* 发光效果 */}
      <span
        className={glowEffectClasses}
        style={{
          boxShadow: '0 0 15px 2px rgba(0, 228, 255, 0.3), 0 0 30px 5px rgba(0, 228, 255, 0.1)',
        }}
      ></span>

      {/* 跟随鼠标的光效 */}
      {mouseFollowEffects}

      {/* 装饰元素 */}
      <span className="absolute inset-0 z-0 overflow-hidden rounded-lg">{decorationElements}</span>

      {/* 扫描线动画 */}
      <span className="absolute inset-0 z-0 overflow-hidden rounded-lg">{scanlineElement}</span>

      {/* 简化的故障效果 */}
      {simpleGlitchEffect}

      {/* 点击波纹 */}
      <span ref={rippleRef} className="absolute inset-0 z-0 overflow-hidden rounded-lg"></span>

      {/* 按钮内容 */}
      {buttonContent}
    </Tag>
  );
};

export default NeonButton;

// 全局样式内容
const styleContent = `
  .ripple {
    position: absolute;
    border-radius: 50%;
    transform: scale(0);
    animation: ripple 0.6s linear;
    background-color: rgba(0, 228, 255, 0.15);
  }
  
  @keyframes ripple {
    to {
      transform: scale(2);
      opacity: 0;
    }
  }
  
  .bg-grid-lines {
    background-image: 
      linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
    background-size: 20px 20px;
  }
  
  .bg-noise {
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
  }
`;

// 在客户端环境中添加全局样式
if (typeof document !== 'undefined') {
  // 检查样式是否已存在
  const existingStyle = document.getElementById('neon-button-style');
  if (!existingStyle) {
    const style = document.createElement('style');
    style.id = 'neon-button-style';
    style.textContent = styleContent;
    document.head.appendChild(style);
  }
}
